import { useEffect } from 'react';

import { readInstalledDisplayMode } from '../pwa/detectStandaloneDisplay';
import { hasInstalledMarker, markInstalled } from '../pwa/pwa-install-storage';
import {
  BeforeInstallPromptEvent,
  usePwaStore,
} from '../stores/usePwaStore';

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const DISPLAY_MODE_QUERIES = [
  '(display-mode: standalone)',
  '(display-mode: fullscreen)',
  '(display-mode: minimal-ui)',
] as const;

function subscribeDisplayModeChanges(onChange: () => void) {
  const media = DISPLAY_MODE_QUERIES.map((query) => window.matchMedia(query));
  media.forEach((entry) => entry.addEventListener('change', onChange));
  return () => {
    media.forEach((entry) => entry.removeEventListener('change', onChange));
  };
}

function applyInstalledState(
  setIsInstalled: (value: boolean) => void,
  setInstallPromptEvent: (event: BeforeInstallPromptEvent | null) => void
) {
  const fromDisplay = readInstalledDisplayMode();
  const fromMarker = hasInstalledMarker();
  if (!fromDisplay && !fromMarker) return false;
  if (fromDisplay) markInstalled();
  setIsInstalled(true);
  setInstallPromptEvent(null);
  return true;
}

export function usePwaInstallListener(shouldCapturePrompt = true) {
  const setInstallPromptEvent = usePwaStore((s) => s.setInstallPromptEvent);
  const setIsInstalled = usePwaStore((s) => s.setIsInstalled);

  useEffect(() => {
    const applyInstalled = () =>
      applyInstalledState(setIsInstalled, setInstallPromptEvent);

    const alreadyInstalled = applyInstalled();
    const unsubscribeDisplayMode = subscribeDisplayModeChanges(applyInstalled);

    if (alreadyInstalled || !shouldCapturePrompt) {
      return unsubscribeDisplayMode;
    }

    const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      setInstallPromptEvent(event);
    };

    const handleAppInstalled = () => {
      markInstalled();
      setIsInstalled(true);
      setInstallPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      unsubscribeDisplayMode();
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [shouldCapturePrompt, setInstallPromptEvent, setIsInstalled]);
}
